import { useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { AddSquare, Refresh, TickCircle } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage, fetchSelectOptions, type SelectOption } from '../api/client';
import { ErrorBlock, LoadingBlock } from '../components/StateBlock';
import { t } from '../i18n';

type PendingStatus = 'PENDING' | 'ASSIGNED' | 'REJECTED';

type PendingDevice = {
  id: string;
  imei: string;
  mac?: string | null;
  device_type?: number | null;
  app_version?: string | null;
  login_attempts: number;
  status: PendingStatus;
  first_seen_at: string;
  last_seen_at: string;
  last_cashier?: { name?: string; cashier_no?: string } | null;
  assigned_register?: { code?: string; name?: string } | null;
};

type AssignmentForm = {
  location_id: string;
  name: string;
  device_type: number;
  deployment_category_ids: string[];
};

const emptyAssignment: AssignmentForm = {
  location_id: '',
  name: '',
  device_type: 100,
  deployment_category_ids: []
};

export function PendingRegisterDevicesPage() {
  const [devices, setDevices] = useState<PendingDevice[]>([]);
  const [locations, setLocations] = useState<SelectOption[]>([]);
  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [status, setStatus] = useState<PendingStatus | 'ALL'>('PENDING');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<PendingDevice | null>(null);
  const [form, setForm] = useState<AssignmentForm>(emptyAssignment);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get<{ data: PendingDevice[] }>('pending-register-devices', {
        params: status === 'ALL' ? {} : { status }
      });
      setDevices(response.data.data ?? []);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, t('pendingRegisters.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    void fetchSelectOptions('locations/search').then(setLocations).catch(() => setLocations([]));
    void fetchSelectOptions('all_categories/search').then(setCategories).catch(() => setCategories([]));
  }, []);

  const openAssignment = (device: PendingDevice) => {
    setSelected(device);
    setForm({
      ...emptyAssignment,
      name: `${t('pendingRegisters.defaultName')} ${device.imei.slice(-4)}`,
      device_type: device.device_type ?? 100
    });
  };

  const assignDevice = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await adminApi.post(`pending-register-devices/${selected.id}/assign`, form);
      setToast({ message: t('pendingRegisters.assignedSuccess'), severity: 'success' });
      setSelected(null);
      await loadDevices();
    } catch (requestError) {
      setToast({ message: apiErrorMessage(requestError, t('pendingRegisters.assignFailed')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (device: PendingDevice, action: 'reject' | 'reopen') => {
    if (action === 'reject' && !window.confirm(t('pendingRegisters.rejectConfirm'))) return;
    try {
      await adminApi.post(`pending-register-devices/${device.id}/${action}`);
      setToast({
        message: action === 'reject' ? t('pendingRegisters.rejectedSuccess') : t('pendingRegisters.reopenedSuccess'),
        severity: 'success'
      });
      await loadDevices();
    } catch (requestError) {
      setToast({ message: apiErrorMessage(requestError, t('pendingRegisters.actionFailed')), severity: 'error' });
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('pendingRegisters.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('pendingRegisters.description')}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>{t('pendingRegisters.statusFilter')}</InputLabel>
            <Select
              value={status}
              label={t('pendingRegisters.statusFilter')}
              onChange={(event) => setStatus(event.target.value as PendingStatus | 'ALL')}
            >
              <MenuItem value="PENDING">{t('pendingRegisters.status.pending')}</MenuItem>
              <MenuItem value="ASSIGNED">{t('pendingRegisters.status.assigned')}</MenuItem>
              <MenuItem value="REJECTED">{t('pendingRegisters.status.rejected')}</MenuItem>
              <MenuItem value="ALL">{t('pendingRegisters.status.all')}</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => void loadDevices()} startIcon={<Refresh size={18} />}>
            {t('action.refresh')}
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info">{t('pendingRegisters.info')}</Alert>

      {loading ? <LoadingBlock /> : null}
      {!loading && error ? <ErrorBlock message={error} /> : null}
      {!loading && !error ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('pendingRegisters.imei')}</TableCell>
                <TableCell>{t('pendingRegisters.device')}</TableCell>
                <TableCell>{t('pendingRegisters.lastCashier')}</TableCell>
                <TableCell>{t('pendingRegisters.lastSeen')}</TableCell>
                <TableCell>{t('pendingRegisters.attempts')}</TableCell>
                <TableCell>{t('pendingRegisters.statusLabel')}</TableCell>
                <TableCell align="right">{t('pendingRegisters.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{device.imei}</Typography>
                    <Typography variant="caption" color="text.secondary">{device.mac || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{device.app_version || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {device.device_type ? t('pendingRegisters.deviceTypeValue', { type: device.device_type }) : t('pendingRegisters.deviceTypeUnknown')}
                    </Typography>
                  </TableCell>
                  <TableCell>{device.last_cashier?.name || device.last_cashier?.cashier_no || '—'}</TableCell>
                  <TableCell>{new Date(device.last_seen_at).toLocaleString()}</TableCell>
                  <TableCell>{device.login_attempts}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={device.status === 'PENDING' ? 'warning' : device.status === 'ASSIGNED' ? 'success' : 'default'}
                      label={t(`pendingRegisters.status.${device.status.toLowerCase()}`)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {device.status === 'PENDING' ? (
                        <>
                          <Button size="small" variant="contained" onClick={() => openAssignment(device)} startIcon={<AddSquare size={16} />}>
                            {t('pendingRegisters.assign')}
                          </Button>
                          <Button size="small" color="error" onClick={() => void changeStatus(device, 'reject')}>
                            {t('pendingRegisters.reject')}
                          </Button>
                        </>
                      ) : null}
                      {device.status === 'REJECTED' ? (
                        <Button size="small" onClick={() => void changeStatus(device, 'reopen')}>
                          {t('pendingRegisters.reopen')}
                        </Button>
                      ) : null}
                      {device.status === 'ASSIGNED' ? (
                        <Typography variant="body2" color="success.main">
                          {device.assigned_register?.name || device.assigned_register?.code || t('pendingRegisters.assigned')}
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {t('pendingRegisters.empty')}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      <Dialog open={Boolean(selected)} onClose={() => !saving && setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('pendingRegisters.assignTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">{t('pendingRegisters.assignDescription', { imei: selected?.imei ?? '' })}</Alert>
            <FormControl fullWidth required>
              <InputLabel>{t('pendingRegisters.location')}</InputLabel>
              <Select
                value={form.location_id}
                label={t('pendingRegisters.location')}
                onChange={(event) => setForm((current) => ({ ...current, location_id: String(event.target.value) }))}
              >
                {locations.map((location) => <MenuItem key={location.value} value={location.value}>{location.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              required
              label={t('pendingRegisters.registerName')}
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>{t('pendingRegisters.deviceType')}</InputLabel>
              <Select
                value={form.device_type}
                label={t('pendingRegisters.deviceType')}
                onChange={(event) => setForm((current) => ({ ...current, device_type: Number(event.target.value) }))}
              >
                <MenuItem value={100}>{t('register.main')}</MenuItem>
                <MenuItem value={101}>{t('register.secondary')}</MenuItem>
                <MenuItem value={200}>{t('register.waiter')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('pendingRegisters.itemCategories')}</InputLabel>
              <Select
                multiple
                value={form.deployment_category_ids}
                label={t('pendingRegisters.itemCategories')}
                renderValue={(values) => values
                  .map((value) => categories.find((category) => String(category.value) === value)?.label ?? value)
                  .join(', ')}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  deployment_category_ids: typeof event.target.value === 'string'
                    ? event.target.value.split(',')
                    : event.target.value.map(String)
                }))}
              >
                {categories.map((category) => (
                  <MenuItem key={category.value} value={String(category.value)}>{category.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} disabled={saving}>{t('action.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => void assignDevice()}
            disabled={saving || !form.location_id || !form.name.trim()}
            startIcon={<TickCircle size={18} />}
          >
            {saving ? t('action.saving') : t('pendingRegisters.completeAssignment')}
          </Button>
        </DialogActions>
      </Dialog>

      {toast ? (
        <Snackbar open autoHideDuration={4000} onClose={() => setToast(null)}>
          <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert>
        </Snackbar>
      ) : null}
    </Stack>
  );
}
